// components/Footer.jsx
import React, { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useEnquiryModal } from "./EnquiryModal";
import { useLeadForm } from "../hooks/useLeadForm";
import { Link, useNavigate } from "react-router-dom";
import { ENQUIRY_CTA } from "../constants";
import { getBasename } from "../utils/getBasename";

const Footer = () => {
  const { openModal } = useEnquiryModal();
  const navigate = useNavigate();   // initialize navigate

  const {
    phone,
    setPhone,
    dialCode,
    isoCode,
    loading,
    success,
    setSuccess,
    submitLead,
    setDialCode,
    setIsoCode,
    setNumberWithoutCountryCode
  } = useLeadForm();

  const [consentChecked, setConsentChecked] = useState(true);
  const [errors, setErrors] = useState({ name: "", phone: "", consent: "" });

  const handleFormSubmit = async (e, cta) => {
    e.preventDefault();

    const name = e.target.name.value.trim();
    const email = e.target.email.value.trim();
    const cleanedPhone = phone ? phone.replace(/\D/g, "") : "";

    const newErrors = { name: "", phone: "", consent: "" };

    // ✅ Name validation
    if (!name) {
      newErrors.name = "Name is required";
    }

    // ✅ Phone validation (strong)
    if (!cleanedPhone) {
      newErrors.phone = "Phone number is required";
    }
    // Allow country code but ensure at least 8 digits total (including code)
    else if (cleanedPhone.length < 8) {
      newErrors.phone = "Please enter a valid phone number";
    }

    // ✅ Consent validation
    if (!consentChecked) {
      newErrors.consent = "You must agree before submitting";
    }

    // Stop form if errors exist
    if (newErrors.name || newErrors.phone || newErrors.consent) {
      setErrors(newErrors);
      return;
    }

    // ✅ No errors — clear errors and submit
    setErrors({ name: "", phone: "", consent: "" });
    const formData = { name, email, phone: cleanedPhone, cta };

    const result = await submitLead(formData, e?.target);
    console.log("submitLead footer", formData)
    if (result) {
      e.target.reset();
      setConsentChecked(false);
      window.location.href = `${getBasename()}/thankyou.html`;
    }
  };

  return (
    <div>
      <footer id="contact" className="w-full bg-[#997736] p-6 sm:p-8 lg:p-10">
        <div className="container mx-auto">
          {/* Desktop Footer */}
          {/* ===== Desktop View ===== */}
          <div className="hidden lg:flex flex-col items-center">
            <h2 className="text-white text-2xl font-bold mb-6">ENQUIRE NOW</h2>
            <form
              onSubmit={(e) => handleFormSubmit(e, ENQUIRY_CTA.FOOTER)}
              className="flex items-end justify-center gap-4 w-full max-w-5xl"
            >
              {/* Name */}
              <div className="flex-1 flex flex-col relative">
                <label className="text-white mb-2 text-sm font-medium">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  className="h-[52px] px-4 rounded-lg bg-white border border-gray-300 focus:border-[#b38e5d] outline-none w-full"
                  required
                />
                {errors.name && (
                  <span className="absolute bottom-[-18px] text-white text-sm mt-1">{errors.name}</span>
                )}
              </div>

              {/* Phone */}
              <div className="flex-1 flex flex-col relative">
                <label className="text-white mb-2 text-sm font-medium">
                  Phone Number
                </label>
                <PhoneInput
                  country={isoCode || "in"}
                  value={phone}
                  onChange={(value, country) => {
                    setPhone(value);
                    setDialCode(`${country.dialCode}`);
                    setIsoCode(country.countryCode);
                    setNumberWithoutCountryCode(
                      value.replace(country.dialCode, "")
                    );
                  }}
                  inputProps={{ name: "phone", required: true }}
                  containerClass="w-full"
                  inputClass="!w-full !h-[52px] !pl-14 !pr-3 !rounded-lg !bg-white !text-black !border !border-gray-300 focus:!border-[#b38e5d] !outline-none placeholder:!text-gray-400"
                  buttonClass="!h-[52px] !bg-gray-200 !border-none !rounded-l-lg"
                  dropdownClass="!bg-white !text-black"
                  searchClass="!bg-white !text-black"
                  enableSearch
                  placeholder="Enter phone number"
                />
                {errors.phone && (
                  <span className="absolute bottom-[-18px] text-white text-sm mt-1">{errors.phone}</span>
                )}
              </div>

              {/* Email */}
              <div className="flex-1 flex flex-col">
                <label className="text-white mb-2 text-sm font-medium">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="h-[52px] px-4 rounded-lg bg-white border border-gray-300 focus:border-[#b38e5d] outline-none w-full"
                />
              </div>

              <button
                type="submit"
                className="bg-[#b38e5d] text-white px-10 py-3 rounded-lg font-semibold hover:bg-[#a07e4d] transition-all h-[52px]"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Enquire"}
              </button>
            </form>

            {/* Consent Text */}
            <div className="relative flex items-start space-x-2 text-gray-200 text-sm text-center mt-4 max-w-2xl">
              <input
                type="checkbox"
                id="consent"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#b38e5d] cursor-pointer"
              />
              <label
                htmlFor="consent"
                className="text-xs leading-relaxed cursor-pointer"
              >
                I agree to the{" "}
                <Link
                  to="/privacy-policy"
                  className="underline hover:text-white"
                >
                  Privacy Policy
                </Link>{" "}
                and consent to be contacted about this project.
              </label>
              {errors.consent && (
                <span className="absolute bottom-[-18px] text-white text-sm mt-1">{errors.consent}</span>
              )}
            </div>

            {/* Disclaimer Text */}
            <div className="text-gray-200 text-center text-sm max-w-2xl mt-4">
              <p className="text-gray-200 text-sm">
                Disclaimer : We are an authorised marketing partner for this project. Provided content is given by respective owners and this website and content is for information purpose only and it does not constitute any offer to avail for any services. Prices mentioned are subject to change without prior notice and properties mentioned are subject to availability. You can expect a call, SMS or emails on details registered with us.
              </p>
            </div>
          </div>

          {/* Mobile/Tablet Footer */}
          <div className="lg:hidden flex flex-col items-center space-y-4">
            <h2 className="text-white text-2xl font-bold">ENQUIRE NOW</h2>
            <form onSubmit={(e) => handleFormSubmit(e, ENQUIRY_CTA.FOOTER)} className="w-full space-y-4 pb-20">
              <div className="flex flex-col">
                <label className="text-white mb-1 text-sm font-medium">Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter your name"
                  className="h-[48px] w-full p-3 rounded-lg bg-white border border-gray-300 focus:border-[#997736] outline-none"
                  required
                />
                {errors.name && (
                  <span className="text-white text-sm mt-1">{errors.name}</span>
                )}
              </div>

              <div className="flex flex-col">
                <label className="text-white mb-1 text-sm font-medium">Phone Number</label>
                <PhoneInput
                  country={isoCode || "in"}
                  value={phone}
                  onChange={(value, country) => {
                    setPhone(value);
                    setDialCode(`${country.dialCode}`);
                    setIsoCode(country.countryCode);
                    setNumberWithoutCountryCode(value.replace(country.dialCode, ""));
                  }}
                  inputProps={{ name: "phone", required: true }}
                  containerClass="w-full "   // ✅ relative so dropdown positions properly
                  // inputClass="!w-full !h-[48px] !pl-12 !pr-3 !rounded-lg !bg-white !text-black !border !border-gray-300 focus:!border-[#997736] !outline-none placeholder:!text-gray-400"
                  // buttonClass="!h-[48px] !flex !items-center !justify-center !bg-gray-200 !border-none !rounded-l-lg !px-1"
                  inputClass="!w-full !bg-white !text-black !py-3 !pl-12 !pr-3 !rounded-md !border !border-gray-300 focus:!border-[#997736] !outline-none"
                  buttonClass="!bg-gray-800 !border-none !rounded-l-md !bg-gray-200"
                  dropdownClass="!bg-white !text-black "   // ✅ keep dropdown above !z-[9999] !absolute
                  searchClass="!bg-white !text-black"
                  enableSearch={true}
                  required
                // placeholder="Enter phone number"
                // dropdownStyle={{ position: "absolute", zIndex: 9999 }}  // ✅ important
                />
                {errors.phone && (
                  <span className="text-white text-sm mt-1">{errors.phone}</span>
                )}
              </div>

              <div className="flex flex-col">
                <label className="text-white mb-1 text-sm font-medium">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  className="h-[48px] w-full p-3 rounded-lg bg-white border border-gray-300 focus:border-[#997736] outline-none"
                // required
                />
              </div>

              <div className="relative flex items-start space-x-1 text-gray-200 text-xs text-center max-w-2xl mt-2">
                <input
                  type="checkbox"
                  id="consent"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#997736] cursor-pointer"
                  required
                />
                <label
                  htmlFor="consent"
                  className="text-xs text-gray-200 leading-relaxed cursor-pointer"
                >
                  I agree to the{" "}
                  <Link
                    to="/privacy-policy"
                    // onClick={onClose}
                    className="underline hover:text-white"
                  >
                    Privacy Policy
                  </Link>{" "}
                  and consent to be contacted about this project.
                </label>
                {errors.consent && (
                  <span className="absolute bottom-[-18px] text-white text-sm mt-1">{errors.consent}</span>
                )}
              </div>

              {/* Disclaimer Text */}
              <div className="text-gray-200 text-center text-xs max-w-2xl mt-2">
                <p className="text-gray-200 text-sm">
                  Disclaimer : We are an authorised marketing partner for this project. Provided content is given by respective owners and this website and content is for information purpose only and it does not constitute any offer to avail for any services. Prices mentioned are subject to change without prior notice and properties mentioned are subject to availability. You can expect a call, SMS or emails on details registered with us.
                </p>
              </div>

              <button
                type="submit"
                className="w-full bg-[#b38e5d] text-white px-8 py-3 rounded-lg font-bold hover:bg-[#a07e4d] transition-colors"
                disabled={loading}
              >
                {loading ? "Submitting..." : "Enquire"}
              </button>
            </form>
          </div>
        </div>
      </footer>

      {/* Fixed Mobile Footer Buttons */}
      <div className="fixed bottom-0 left-0 w-full bg-[#997736] shadow-md lg:hidden z-50">
        <div className="flex justify-around w-full p-2 space-x-2">
          {/* <button onClick={openModal} className="flex-1 flex flex-col items-center p-2 rounded-lg bg-[#b38e5d] text-white hover:bg-[#a07e4d] transition-colors">
            <i className="fas fa-phone-alt text-lg transform rotate-90"></i>
            <span className="text-sm mt-1">CALL</span>
          </button> */}
          <button onClick={() => openModal(ENQUIRY_CTA.FOOTER)} className="flex-1 flex flex-col items-center p-2 rounded-lg bg-[#b38e5d] text-white hover:bg-[#a07e4d] transition-colors">
            <i className="fas fa-envelope text-lg"></i>
            <span className="text-sm mt-1">ENQUIRY</span>
          </button>
          {/* <button onClick={openModal} className="flex-1 flex flex-col items-center p-2 rounded-lg bg-[#b38e5d] text-white hover:bg-[#a07e4d] transition-colors">
            <i className="fab fa-whatsapp text-lg"></i>
            <span className="text-sm mt-1">WHATSAPP</span>
          </button> */}
        </div>
      </div>

      {/* {success && (
        <div className="alert-modal fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg text-center max-w-sm">
            <p className="mb-4">Thank you for your enquiry! We will get back to you shortly.</p>
            <button
              onClick={() => setSuccess(false)}
              className="bg-[#b38e5d] text-white px-6 py-2 rounded-lg hover:bg-[#a07e4d] transition-colors"
            >
              OK
            </button>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default Footer;